import { Dayjs } from "utils/dayjs"

import { Guid, Lmid } from "@/types"
import { toDateTimeWithOffset } from "utils/utils"
import ReservationAsset, { IGetReservationAssetDto } from "./ReservationAsset"
import i18n from "intl/i18n"
import { ShippingOrderStatus } from "./ShippingOrder"
import { Purpose } from "config/constants"

export interface IGetReservationCartAssetDto extends IGetReservationAssetDto {
    id?: Guid
    assetUnavailable: boolean
    preferredWarehouseId?: Guid | null
}

export interface IAddReservationCartAssets {
    assetIds: Guid[]
    start?: Dayjs | null
    end?: Dayjs | null
}

export interface IUpdateReservationCartAsset {
    id: Guid
    assetId?: Guid | null
    start?: Dayjs | null
    end?: Dayjs | null
    preferredWarehouseId?: Guid | null
}

export interface IUpdateReservationCartAssetWarehouse {
    id: Guid
    preferredWarehouseId: Guid | null
}

export interface IUpdateReservationCartAssetDto extends Omit<IUpdateReservationCartAsset, "start" | "end"> {
    start?: string | null
    end?: string | null
}

export interface IReservationCartAssetWithInvalidPeriod {
    assetId: Guid
    lmid: Lmid
    articleDescription: string
    altArticleDescription?: string
    isLabeled?: boolean
}

interface IReservationCartAssetPeriodValidationParams {
    reservationStart: Dayjs
    reservationEnd: Dayjs | null
    assetStart: Dayjs | null
    assetEnd: Dayjs | null
    lastNotCreditedInvoiceEndDate: Dayjs | null
    isNodeAssetWithInvoiceBeforeShipping?: boolean
    purpose?: Purpose
    isReservationLockedOrClosed?: boolean
    disposalDate?: Dayjs | null
}

interface IReservationCartAssetDateState {
    isDisabled: boolean
    isUseRequestDateDisabled: boolean
    validationMessage: string | null
}

export default class ReservationCartAsset extends ReservationAsset {
    itemId?: Guid
    assetUnavailable: boolean
    preferredWarehouseId?: Guid | null

    constructor(dto: IGetReservationCartAssetDto) {
        super(dto)
        this.itemId = dto.id
        this.assetUnavailable = dto.assetUnavailable
        this.preferredWarehouseId = dto.preferredWarehouseId ?? null
    }

    get isAvailable(): boolean {
        return !this.isShortage && !this.assetUnavailable
    }

    get isShortage(): boolean {
        return !this.assetId
    }

    get hasBeenReleased(): boolean {
        if (!this.shipping || this.shipping.length === 0) return false

        for (const shippingItem of this.shipping) {
            if (shippingItem.shippingOrderAsset.releaseDate) {
                return true
            }
        }

        return false
    }

    /**
     * Hover text explaining why the preferred warehouse cannot be changed, or null when it can.
     *
     * The rules are evaluated in order and the first match wins: an asset released within the
     * current request (now or earlier) outranks a fixed asset, so a fixed asset that has already
     * been released reports the release reason. Everything else keeps the preference editable.
     *
     * The release check deliberately uses `hasBeenReleased` — derived from `shipping`, which holds
     * only the current request's shipping orders — and not `stateOfStorage`, which carries the
     * asset-global display status. An asset released in *another* reservation must stay editable.
     */
    get preferredWarehouseDisabledReason(): string | null {
        if (this.hasBeenReleased) return i18n.t("reservation.preferredWarehouseDisabledReleasedInCr")
        if (this.isFixed) return i18n.t("reservation.preferredWarehouseDisabledFixed")
        return null
    }

    static getUpdateDto(data: IUpdateReservationCartAsset): IUpdateReservationCartAssetDto {
        return {
            ...data,
            start: data.start === null ? null : (data.start ? toDateTimeWithOffset(data.start) : undefined),
            end: data.end === null ? null : (data.end ? toDateTimeWithOffset(data.end) : undefined),
        }
    }

    hasInvalidPeriod(params: IReservationCartAssetPeriodValidationParams): boolean {
        return !!this.getStartDateState(params).validationMessage
            || !!this.getEndDateState(params).validationMessage
    }

    get hasActiveShippingOrder(): boolean {
        return this.shipping?.some(so => so.shippingOrderStatus !== ShippingOrderStatus.completed) || false
    }

    getStartDateState({
        reservationStart,
        reservationEnd,
        lastNotCreditedInvoiceEndDate,
        assetStart,
        assetEnd,
        isNodeAssetWithInvoiceBeforeShipping,
        purpose,
        isReservationLockedOrClosed,
        disposalDate,
    }: IReservationCartAssetPeriodValidationParams): IReservationCartAssetDateState {
        const lastNotCreditedInvoiceEndDateNextDay = lastNotCreditedInvoiceEndDate?.clone().add(1, "day").startOf("day")
        const isAssetStartInitInsideOfInvoicedPeriod = !!lastNotCreditedInvoiceEndDate && (!this.start || !!this.start.isSameOrBefore(lastNotCreditedInvoiceEndDate.clone().endOf("day")))
        const isStartDateDisabled = purpose === Purpose.sale
            ? !!isReservationLockedOrClosed || !!disposalDate
            : (isNodeAssetWithInvoiceBeforeShipping || (!!lastNotCreditedInvoiceEndDate && this.hasBeenReleased && isAssetStartInitInsideOfInvoicedPeriod))
        const isUseRequestStartDateDisabled = purpose === Purpose.sale
            ? !!isReservationLockedOrClosed || !!disposalDate
            : (isNodeAssetWithInvoiceBeforeShipping || (!!lastNotCreditedInvoiceEndDate && this.hasBeenReleased))

        let validationMessage: string | null = null

        if (!isStartDateDisabled) {
            if (purpose !== Purpose.sale && this.hasBeenReleased && lastNotCreditedInvoiceEndDateNextDay && assetStart?.isBefore(lastNotCreditedInvoiceEndDateNextDay)) {
                validationMessage = i18n.t("changePeriod.startDateInvoicePeriodValidation", { date: lastNotCreditedInvoiceEndDate?.format(i18n.t("config.dateLongFormat")) })
            } else if (assetStart?.isBefore(reservationStart)) {
                validationMessage = i18n.t("changePeriod.startDateRequestStartValidation", { date: reservationStart.format(i18n.t("config.dateLongTimeFormat")) })
            } else if (assetStart?.isSameOrAfter(assetEnd || reservationEnd)) {
                validationMessage = i18n.t("changePeriod.startDateEndDateValidation")
            }
        }

        return {
            isDisabled: isStartDateDisabled,
            isUseRequestDateDisabled: isUseRequestStartDateDisabled,
            validationMessage,
        }
    }

    getEndDateState({
        reservationStart,
        reservationEnd,
        lastNotCreditedInvoiceEndDate,
        assetStart,
        assetEnd,
        isNodeAssetWithInvoiceBeforeShipping,
    }: IReservationCartAssetPeriodValidationParams): IReservationCartAssetDateState {
        const isReservationFullyInvoiced = !!lastNotCreditedInvoiceEndDate && !!reservationEnd && reservationEnd.isSame(lastNotCreditedInvoiceEndDate, "date")
        const isEndDateDisabled = !reservationEnd || (isReservationFullyInvoiced && this.hasBeenReleased)
        const isUseRequestEndDateDisabled = isReservationFullyInvoiced && this.hasBeenReleased
        const lastNotCreditedInvoiceEndDateEndOfDay = lastNotCreditedInvoiceEndDate?.clone().hour(23).minute(45).startOf("minute")

        let validationMessage: string | null = null

        if (!isEndDateDisabled) {
            if (reservationEnd && assetEnd?.isAfter(reservationEnd)) {
                validationMessage = i18n.t("changePeriod.endDateRequestEndValidation", { date: reservationEnd.format(i18n.t("config.dateLongTimeFormat")) })
            } else if (assetEnd?.isSameOrBefore(assetStart || reservationStart)) {
                validationMessage = i18n.t("changePeriod.endDateStartDateValidation")
            } else if ((this.hasBeenReleased || isNodeAssetWithInvoiceBeforeShipping) && lastNotCreditedInvoiceEndDateEndOfDay && assetEnd && !assetEnd.isSame(this.end) && assetEnd.isBefore(lastNotCreditedInvoiceEndDateEndOfDay)) {
                validationMessage = i18n.t("changePeriod.endDateInvoicePeriodValidation", { date: lastNotCreditedInvoiceEndDateEndOfDay?.format(i18n.t("config.dateLongTimeFormat")) })
            }
        }

        return {
            isDisabled: isEndDateDisabled,
            isUseRequestDateDisabled: isUseRequestEndDateDisabled,
            validationMessage,
        }
    }

    get canChange(): boolean {
        return !this.isPushedToStock && !this.isReleasedOrStored && !this.unplanned
    }

    get isAutomaticSelection(): boolean {
        return !this.assetUnavailable
            && !!this.assetId
            && !this.hasUserAssignment
            && !this.isReleasedOrStored
    }

    get needsFixedAssetSelection(): boolean {
        return this.isAutomaticSelection || this.isShortage
    }
}
